import { JSX, useEffect } from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import { SiteInfo, StaticPath } from '@sitecore-content-sdk/nextjs';
import { extractPath, handleEditorFastRefresh } from '@sitecore-content-sdk/nextjs/utils';
import { SitecorePageProps } from '@sitecore-content-sdk/nextjs';
import HandleMockError, { MockErrorData } from 'helpers/ErrorHandling/HandleMockError';
import Layout from 'src/Layout';
import NotFound from 'src/NotFound';
import { getPageData, PageData } from 'lib/utils/page-data-static-props/get-page-data';
import Providers from './Providers';
import { isDesignLibraryPreviewData } from '@sitecore-content-sdk/nextjs/editing';
import scConfig from 'sitecore.config';
import sites from '.sitecore/sites.json';
import components from '.sitecore/component-map';
import client from 'lib/sitecore-client';
import {
  EMPTY_PAGE_DATA,
  PageDataProvider,
} from 'lib/utils/page-data-static-props/pageDataContext';
import { buildSchemaForRoute } from 'lib/schema-builders/schemaRouter';
import StructuredData from 'components/StructuredData';
import { usePathname } from 'next/navigation';

// Extend SitecorePageProps to include our custom PageData that is passed to each route
interface ExtendedSitecorePageProps extends SitecorePageProps {
  mockError?: MockErrorData;
  customPageData?: PageData;
}

const SitecorePage = ({
  notFound,
  componentProps,
  page,
  mockError,
  customPageData,
}: ExtendedSitecorePageProps): JSX.Element => {
  const pagePath = usePathname();
  useEffect(() => {
    // Since Sitecore editors do not support Fast Refresh, need to refresh editor chromes after Fast Refresh finished
    handleEditorFastRefresh();
  }, []);

  if (mockError) {
    return <HandleMockError {...mockError} />;
  }

  if (notFound || !page) {
    // Shouldn't hit this (as long as 'notFound' is being returned below), but just to be safe
    return <NotFound />;
  }

  const pageSchema = buildSchemaForRoute({ page, pagePath });

  return (
    <Providers componentProps={componentProps} page={page}>
      <PageDataProvider pageDataFromStaticProps={customPageData || EMPTY_PAGE_DATA}>
        {pageSchema && <StructuredData data={pageSchema} />}
        <Layout page={page} />
      </PageDataProvider>
    </Providers>
  );
};

export const getStaticPaths: GetStaticPaths = async (context) => {
  // Fallback, along with revalidate in getStaticProps (below),
  // enables Incremental Static Regeneration. This allows us to
  // leave certain (or all) paths empty if desired and static pages
  // will be generated on request (development mode in this example).
  // Alternatively, the entire sitemap could be pre-rendered
  // ahead of time (non-development mode in this example).
  // See https://nextjs.org/docs/basic-features/data-fetching/incremental-static-regeneration

  let paths: StaticPath[] = [];
  let fallback: boolean | 'blocking' = 'blocking';

  // We are not generating any pages on build, with GENERATE_STATIC_PATHS set to false for all envs
  if (process.env.NODE_ENV !== 'development' && scConfig.generateStaticPaths) {
    try {
      paths = await client.getPagePaths(
        sites.map((site: SiteInfo) => site.name),
        context?.locales || []
      );
    } catch (error) {
      console.log('Error occurred while fetching static paths');
      console.log(error);
    }

    fallback = process.env.EXPORT_MODE ? false : fallback;
  }

  return {
    paths,
    fallback,
  };
};

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation (or fallback) is enabled and a new request comes in.
export const getStaticProps: GetStaticProps = async (context) => {
  let props = {};
  let customPageData: PageData = {};
  const path = extractPath(context);
  let page;

  if (context.preview && isDesignLibraryPreviewData(context.previewData)) {
    page = await client.getDesignLibraryData(context.previewData);
  } else {
    page = context.preview
      ? await client.getPreview(context.previewData)
      : await client.getPage(path, { locale: context.locale });
  }
  if (page) {
    props = {
      page,
      dictionary: await client.getDictionary({
        site: page.siteName,
        locale: page.locale,
      }),
      componentProps: await client.getComponentData(page.layout, context, components),
    };
    customPageData = await getPageData(page);
  }

  return {
    props: {
      ...props,
      customPageData,
    },
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 5 seconds
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 5 seconds
    revalidate: 60, // In seconds
    notFound: !page,
  };
};

export default SitecorePage;
